// Journeys (plan step 3.6, Console D `#/journeys`, APS-316): a funnel over screens, built here
// (2 to 6 steps picked from the app's screens), with each step's sessions, conversion and time
// against the previous release, where the sessions that stopped went, and the worst drop as the
// view's one accent; then the paths after and before one screen, side by side. The URL keeps
// the steps and the screen, so a funnel can be shared. Screen names are the app's: shown as
// text, never translated. Screens come from the screen view events (spec 8.6), or the screen
// load spans of a session without any (spec 8.1): the page says what those miss.
import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { funnelQuery, journeysQuery, screensQuery } from '../api/queries'
import type { Funnel, Journeys } from '../api/types'
import { EmptyNote, LK, PageHead, Section, SectionHead, Seg, SelectChip } from '../components/d'
import { Icon } from '../components/icons'
import {
  deltaMeta,
  NoApp,
  perfMeta,
  usePerfReleases,
  usePerfScope,
  withRelease,
} from '../components/Perf'
import { ErrorState, Loading } from '../components/states'
import { type Translator, useI18n } from '../i18n'
import { duration, percentSpaced } from '../lib/format'
import { deltaPoints, pathRows, pathScreen, worstDrop } from '../lib/journeys'
import { useProjectParams } from './params'
import { carried, FUNNEL_WITHIN, JOURNEY_DEPTHS, type JourneysSearch } from './search'

const MAX_STEPS = 6
const DEFAULT_DEPTH = 1

type Step = Funnel['steps'][number]
type Set = (patch: Partial<JourneysSearch>) => void

/** "+0.2 pts", "−1.6 pts": a change in conversion, one decimal as the mock reads it. */
function pts(i18n: Translator, d: number): string {
  const v = i18n.number(Math.abs(d), { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  return `${d < 0 ? '−' : '+'}${v}`
}

/** A funnel's share as the mock reads it: 100 %, 47.2 %, 6.9 % (at most one decimal). */
function share(i18n: Translator, rate: number): string {
  return `${i18n.number(rate * 100, { maximumFractionDigits: 1 })} %`
}

/** A count as the mock reads it in the funnel: 960, 1.5k, 88.2k, 1.2M. */
function kilo(i18n: Translator, n: number): string {
  const one = (v: number) => i18n.number(v, { maximumFractionDigits: 1 })
  return n >= 1e6 ? `${one(n / 1e6)}M` : n >= 1e3 ? `${one(n / 1e3)}k` : i18n.number(n)
}

/** "5 min", "1 h", "1 d": a funnel's window, as the within chip reads it. */
function windowLabel(t: Translator['t'], minutes: number): string {
  return minutes >= 1440
    ? t('journeys.funnel.days', { n: minutes / 1440 })
    : minutes >= 60
      ? t('journeys.funnel.hours', { n: minutes / 60 })
      : t('journeys.funnel.minutes', { n: minutes })
}

/** The app's screens for the pickers, and any picked one the list does not have. */
function useScreenNames(extra: (string | undefined)[]) {
  const { projectId } = useProjectParams()
  const { scope } = usePerfScope()
  const screens = useQuery({
    ...screensQuery(projectId, scope, 'loads', 200),
    enabled: !!scope.app_id,
  })
  const names = (screens.data ?? []).map((s) => s.screen)
  for (const e of extra) if (e && !names.includes(e)) names.push(e)
  return names
}

function ScreenOptions({ names }: { names: string[] }) {
  return names.map((n) => (
    <option key={n} value={n}>
      {n}
    </option>
  ))
}

/** A screen's name, linking to its Performance › Screens page. */
function ScreenLink({ screen, search }: { screen: string; search: JourneysSearch }) {
  const { orgId, projectId } = useProjectParams()
  return (
    <Link
      className="ul"
      to="/o/$orgId/p/$projectId/performance/screens/detail"
      params={{ orgId, projectId }}
      search={{ ...carried(search), screen }}
      translate="no"
    >
      {screen}
    </Link>
  )
}

/** The funnel's step chips, "+ step" and "within". */
function FunnelTools({
  steps,
  names,
  search,
  set,
}: {
  steps: string[]
  names: string[]
  search: JourneysSearch
  set: Set
}) {
  const i18n = useI18n()
  const { t } = i18n
  const setStep = (i: number, value: string | undefined) => {
    const next = [...steps]
    if (value === undefined) next.splice(i, 1)
    else next[i] = value
    set({ steps: next })
  }
  return (
    // biome-ignore lint/a11y/useSemanticElements: a row of chips, as the mock's tool row
    <div className="jn-tools" role="group" aria-label={t('journeys.funnel.steps')}>
      {steps.map((step, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a step is its position in the funnel
        <span key={i} className="jn-stp">
          {i > 0 ? (
            <span className="jn-ar" aria-hidden="true">
              →
            </span>
          ) : null}
          <SelectChip
            prefix={i + 1}
            label={t('journeys.funnel.step', { n: i + 1 })}
            value={step}
            onChange={(v) => setStep(i, v)}
            translate="no"
            after={
              steps.length > 2 ? (
                <button
                  type="button"
                  aria-label={t('journeys.funnel.remove', { n: i + 1 })}
                  onClick={() => setStep(i, undefined)}
                >
                  <Icon name="x" small />
                </button>
              ) : null
            }
          >
            <option value="">{t('journeys.funnel.pick')}</option>
            <ScreenOptions names={names} />
          </SelectChip>
        </span>
      ))}
      {steps.length < MAX_STEPS ? (
        <button type="button" className="chip add" onClick={() => set({ steps: [...steps, ''] })}>
          {t('journeys.funnel.add')}
        </button>
      ) : null}
      <span className="jn-sp" />
      <SelectChip
        prefix={t('journeys.funnel.within')}
        label={t('journeys.funnel.withinLabel')}
        value={String(search.within ?? '')}
        onChange={(v) => set({ within: v ? (Number(v) as JourneysSearch['within']) : undefined })}
      >
        <option value="">{t('journeys.funnel.session')}</option>
        {FUNNEL_WITHIN.map((m) => (
          <option key={m} value={m}>
            {windowLabel(t, m)}
          </option>
        ))}
      </SelectChip>
    </div>
  )
}

/** Where the sessions that stopped at a step went: "3.2k stopped at Checkout, then: Cart 1.5k…". */
function Stopped({ step }: { step: Step }) {
  const i18n = useI18n()
  const { t } = i18n
  const d = step.dropped
  if (!d || d.sessions === 0) return null
  return (
    <span>
      {t('journeys.funnel.stoppedAt', {
        count: d.sessions,
        n: kilo(i18n, d.sessions),
        screen: step.screen,
      })}
      {d.next_screens.length ? (
        <>
          {t('journeys.funnel.then')}{' '}
          <span className="mono">
            {d.next_screens.map((n, j) => (
              <span key={n.screen}>
                {j > 0 ? ', ' : ''}
                <span translate="no">{n.screen}</span> {kilo(i18n, n.sessions)}
              </span>
            ))}
          </span>
        </>
      ) : null}
      {d.ended > 0 ? (
        <>
          {'; '}
          <span className="mono">{kilo(i18n, d.ended)}</span>{' '}
          {t('journeys.funnel.ended', { count: d.ended })}
        </>
      ) : null}
      .
    </span>
  )
}

function FunnelRows({
  steps,
  deltas,
  previous,
  worst,
  search,
}: {
  steps: Step[]
  /** Each step's change against the previous release, in points; none without one. */
  deltas: (number | undefined)[] | undefined
  previous: string | undefined
  worst: number | undefined
  search: JourneysSearch
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const { scope } = usePerfScope()
  const width = (s: Step | undefined) =>
    `${Math.max(0, Math.min(100, (s?.conversion_from_first ?? 0) * 100))}%`
  const sessions = (extra: { screen?: string; stopped?: string }) => ({
    to: '/o/$orgId/p/$projectId/sessions' as const,
    params: { orgId, projectId },
    // The same range: the sessions list clamps it to the ones it has.
    search: { ...carried(search), app: scope.app_id, ...extra },
  })
  return (
    <div className="jn-fn">
      {steps.map((s, i) => {
        const from = steps[i - 1]
        const d = deltas?.[i]
        const cur = i === worst
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: a step is its position in the funnel
          <div key={i} className="jn-pair">
            {from ? (
              <div className={cur ? 'jn-drop cur' : 'jn-drop'}>
                <span className="jn-cv">
                  <b>{share(i18n, s.conversion_from_previous ?? 0)}</b>{' '}
                  {t('journeys.funnel.of', { screen: from.screen })}
                </span>
                <span className="jn-dd">
                  <span>
                    {s.median_ms_from_previous !== null ? (
                      <>
                        {t('journeys.funnel.median')}{' '}
                        <span className="mono">{duration(i18n, s.median_ms_from_previous)}</span>{' '}
                        {t('journeys.funnel.from', { screen: from.screen })}
                      </>
                    ) : (
                      t('journeys.funnel.noMedian')
                    )}
                    {d !== undefined && previous ? (
                      <>
                        {' · '}
                        <span className={cur && d < 0 ? 'mono c-crash' : 'mono'}>
                          {t('journeys.funnel.pts', { pts: pts(i18n, d) })}
                        </span>{' '}
                        {t('journeys.funnel.vs', { previous })}
                      </>
                    ) : null}
                  </span>
                  <Stopped step={from} />
                  {cur && (from.dropped?.sessions ?? 0) > 0 ? (
                    <span className="jn-act">
                      <Link className={LK} {...sessions({ stopped: from.screen })}>
                        {t('journeys.funnel.stoppedLink', { screen: from.screen })}
                      </Link>
                    </span>
                  ) : null}
                </span>
              </div>
            ) : null}
            <div className="jn-step">
              <span className="jn-sn">
                <span className="faint">{i + 1}</span>
                <ScreenLink screen={s.screen} search={search} />
              </span>
              <span
                className="jn-trk"
                role="img"
                aria-label={t('journeys.funnel.track', {
                  screen: s.screen,
                  share: share(i18n, s.conversion_from_first ?? 0),
                })}
              >
                {from ? <i className="p" style={{ width: width(from) }} /> : null}
                <i className="c" style={{ width: width(s) }} />
              </span>
              <span className="jn-sv">
                {/* The count is the way to the sessions with the screen. */}
                <Link className="ul" {...sessions({ screen: s.screen })}>
                  <b>{kilo(i18n, s.sessions)}</b>{' '}
                  {t('journeys.funnel.sessions', { count: s.sessions })}
                </Link>{' '}
                · {share(i18n, s.conversion_from_first ?? 0)}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function PathList({
  query,
  direction,
  depth,
  search,
}: {
  query: UseQueryResult<Journeys>
  direction: 'next' | 'previous'
  depth: number
  search: JourneysSearch
}) {
  const i18n = useI18n()
  const { t, number } = i18n
  if (query.isPending) return <Loading />
  if (query.isError) return <ErrorState error={query.error} retry={() => query.refetch()} />
  return (
    <div className="jn-pts">
      {pathRows(query.data, depth).map((r) =>
        r.kind === 'other' ? (
          <div key={r.key} className="jn-pt more" style={{ '--d': r.depth } as React.CSSProperties}>
            <span>{t('journeys.tree.other', { count: r.sessions })}</span>
          </div>
        ) : (
          <div
            key={r.key}
            className={[r.depth > 0 ? 'jn-pt ch' : 'jn-pt', r.kind === 'end' ? 'exit' : ''].join(
              ' ',
            )}
            style={{ '--d': r.depth } as React.CSSProperties}
          >
            <span>
              {r.kind === 'end' ? (
                t(direction === 'next' ? 'journeys.tree.end' : 'journeys.tree.start')
              ) : (
                <ScreenLink screen={r.screen} search={search} />
              )}
            </span>
            <span className="jn-b" aria-hidden="true">
              <i style={{ width: `${Math.max(r.share * 100, 0.8)}%` }} />
            </span>
            <span>{number(r.sessions)}</span>
            <span>{percentSpaced(i18n, r.share, 1)}</span>
          </div>
        ),
      )}
    </div>
  )
}

function Paths({
  screen,
  names,
  search,
  set,
}: {
  screen: string | undefined
  names: string[]
  search: JourneysSearch
  set: Set
}) {
  const { t, number } = useI18n()
  const { projectId } = useProjectParams()
  const { scope } = usePerfScope()
  const depth = search.depth ?? DEFAULT_DEPTH
  const enabled = !!screen && !!scope.app_id
  const next = useQuery({
    ...journeysQuery(projectId, scope, screen ?? '', 'next', depth),
    enabled,
  })
  const prev = useQuery({
    ...journeysQuery(projectId, scope, screen ?? '', 'previous', depth),
    enabled,
  })
  const total = next.data?.sessions ?? prev.data?.sessions ?? 0
  return (
    <Section
      title={t('journeys.tree.title')}
      meta={t('journeys.tree.sub')}
      right={
        <span className="jn-right">
          <SelectChip
            prefix={t('journeys.tree.screen')}
            label={t('journeys.tree.screenLabel')}
            value={screen ?? ''}
            onChange={(v) => set({ screen: v || undefined })}
            translate="no"
          >
            {screen ? null : <option value="">{t('journeys.funnel.pick')}</option>}
            <ScreenOptions names={names} />
          </SelectChip>
          <Seg
            label={t('journeys.tree.depth')}
            options={JOURNEY_DEPTHS.map((d) => ({
              value: String(d),
              label: d === 1 ? t('journeys.tree.depthN', { n: number(d) }) : number(d),
            }))}
            value={String(depth)}
            onChange={(v) => set({ depth: Number(v) as JourneysSearch['depth'] })}
          />
        </span>
      }
    >
      {!screen ? (
        <EmptyNote title={t('journeys.tree.empty')} />
      ) : next.data?.sessions === 0 ? (
        <EmptyNote title={t('journeys.tree.none')} />
      ) : (
        <>
          {next.data ? (
            <p className="jn-lead">
              {t('journeys.tree.lead', {
                count: next.data.sessions,
                n: number(next.data.sessions),
                screen,
                ended: number(next.data.ended),
              })}
            </p>
          ) : null}
          <div className="grid-h">
            <div>
              <SectionHead
                minor
                title={t('journeys.tree.next')}
                meta={t('journeys.tree.nextSub', { screen, n: number(total) })}
              />
              <PathList query={next} direction="next" depth={depth} search={search} />
            </div>
            <div>
              <SectionHead
                minor
                title={t('journeys.tree.previous')}
                meta={t('journeys.tree.previousSub', { screen, n: number(total) })}
              />
              <PathList query={prev} direction="previous" depth={depth} search={search} />
            </div>
          </div>
        </>
      )}
      <p className="jn-note">{t('journeys.source')}</p>
    </Section>
  )
}

export function JourneysPage() {
  const i18n = useI18n()
  const { t } = i18n
  const { projectId } = useProjectParams()
  const navigate = useNavigate()
  const { scope, app, appsPending, search } = usePerfScope()
  const s = search as JourneysSearch
  const { current, previous } = usePerfReleases(scope)
  const steps = s.steps ?? ['', '']
  const names = useScreenNames([...steps, s.screen])
  const complete = steps.filter(Boolean)
  const ready = complete.length >= 2 && complete.length === steps.length && !!scope.app_id
  const funnel = useQuery({ ...funnelQuery(projectId, scope, complete, s.within), enabled: ready })
  // The deltas compare the release in scope (the latest when every release is) with the one
  // before it (D103 #14); with one release picked, the first query is the current one.
  const now = useQuery({
    ...funnelQuery(projectId, withRelease(scope, current), complete, s.within),
    enabled: ready && !!previous && !!current,
  })
  const before = useQuery({
    ...funnelQuery(projectId, withRelease(scope, previous), complete, s.within),
    enabled: ready && !!previous,
  })
  const set: Set = (patch) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) })

  const data = funnel.data?.steps
  const shown = data && (data[0]?.sessions ?? 0) > 0 ? data : undefined
  // A change needs both sides of one release pair: the current release's funnel and the one before.
  const cmp = previous && now.data && before.data ? before.data.steps : undefined
  const deltaSteps = cmp && now.data ? now.data.steps : undefined
  const worst = shown ? worstDrop(deltaSteps ?? shown, cmp) : undefined
  const deltas = cmp && deltaSteps ? cmp.map((b, i) => deltaPoints(deltaSteps[i], b)) : undefined
  const worstStep = worst !== undefined ? shown?.[worst] : undefined
  const worstFrom = worst !== undefined ? shown?.[worst - 1] : undefined
  const worstDelta = worst !== undefined ? deltas?.[worst] : undefined
  const meta = perfMeta(
    i18n,
    app,
    scope,
    [
      shown
        ? t('journeys.meta.first', {
            count: shown[0]?.sessions ?? 0,
            n: kilo(i18n, shown[0]?.sessions ?? 0),
            screen: shown[0]?.screen ?? '',
          })
        : null,
      worstStep && worstFrom
        ? [
            t('journeys.meta.drop', {
              from: worstFrom.screen,
              to: worstStep.screen,
              share: share(i18n, worstStep.conversion_from_previous ?? 0),
            }),
            worstDelta !== undefined && previous && Math.abs(worstDelta) >= 0.05
              ? t(worstDelta < 0 ? 'journeys.meta.below' : 'journeys.meta.above', {
                  pts: i18n.number(Math.abs(worstDelta), {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  }),
                  previous,
                })
              : null,
          ]
            .filter(Boolean)
            .join(', ')
        : null,
      // With one release picked, the drop's change names the release it compares with.
      shown && !scope.release ? deltaMeta(t, scope, current, previous) : null,
      // Journeys and funnels take no device filter (APS-228): a carried one is said not to apply.
    ],
    'na',
  )
  const screen = pathScreen(s.screen, shown, worst, names)
  // An app named in the URL is enough to ask for its funnel and paths: the apps list is awaited
  // only to pick the first app, or to say the named one is not in the project.
  const waiting = appsPending && !scope.app_id

  return (
    <main id="main" className="pg jn-pg">
      <PageHead title={t('journeys.title')} meta={app || scope.app_id ? meta : undefined} />
      {waiting ? (
        <Loading />
      ) : !appsPending && !app ? (
        <NoApp />
      ) : (
        <>
          <Section title={t('journeys.funnel.title')} meta={t('journeys.funnel.sub')}>
            <FunnelTools steps={steps} names={names} search={s} set={set} />
            {!ready ? (
              <EmptyNote title={t('journeys.funnel.empty')} />
            ) : funnel.isPending ? (
              <Loading />
            ) : funnel.isError ? (
              <ErrorState error={funnel.error} retry={() => funnel.refetch()} />
            ) : !shown ? (
              <EmptyNote title={t('journeys.funnel.none')} />
            ) : (
              <FunnelRows
                steps={shown}
                deltas={deltas}
                previous={cmp ? previous : undefined}
                worst={worst}
                search={s}
              />
            )}
          </Section>
          <Paths screen={screen} names={names} search={s} set={set} />
        </>
      )}
    </main>
  )
}
